import BusinessDateFormats from '@/modules/common/filters/BusinessDateFormats';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Formats date to ISO format YYYY-MM-DD
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date in YYYY-MM-DD format
 */
export const formatToISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Safely parses a 'YYYY-MM-DD' string into a Date object at noon.
 * This avoids timezone shifts caused by `new Date('YYYY-MM-DD')` which interprets the date as UTC.
 * @param {string|Date} isoDateString - Date string in 'YYYY-MM-DD' format or Date object.
 * @returns {Date | null} - A Date object or null if input is falsy.
 */
export const parseISODateString = (isoDateString) => {
  if (!isoDateString) return null;
  if (isoDateString instanceof Date) return isoDateString;
  return new Date(isoDateString + 'T00:00:00');
};

/**
 * Gets today's date in YYYY-MM-DD format, respecting the user's timezone
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const formatTodayToISODate = () => {
  const today = new Date();
  return formatToISODate(today);
};

/**
 * Calculates the end date of a package based on validity parameters
 * @param {string} validFrom - Starting date in YYYY-MM-DD format
 * @param {number} expiration - Duration number
 * @param {string} expirationUnit - Duration unit (d: days, w: weeks, m: months, y: years)
 * @returns {string} - End date in YYYY-MM-DD format
 */
export const addDurationToDate = (validFrom, expiration, expirationUnit) => {
  const unitKey = expirationUnit;

  const unitsMapping = {
    d: 'days',
    w: 'weeks',
    m: 'months',
    y: 'years',
  };
  const unit = unitsMapping[unitKey];
  if (!unit) return null;

  const date = new Date(validFrom);

  switch (unit) {
    case 'days':
      date.setDate(date.getDate() + expiration);
      break;
    case 'weeks':
      date.setDate(date.getDate() + (expiration * 7));
      break;
    case 'months':
      date.setMonth(date.getMonth() + expiration);
      date.setDate(date.getDate() - 1);
      break;
    case 'years':
      date.setFullYear(date.getFullYear() + expiration);
      date.setDate(date.getDate() - 1);
      break;
    default:
      return null;
  }

  return formatToISODate(date);
};

/**
 * Formats a date in "day month year" format, always showing the year
 * @param {string|Date} date - Date to format
 * @param {boolean} isFullMonth - Whether to use full month name
 * @returns {string} - Formatted date in "day month year" format
 */
export const formatDateWithYear = (date, isFullMonth = true) => {
  if (!date) return '';
  
  // Convert to Date object if it's a string
  let dateObj;
  if (typeof date === 'string') {
    // If it's already an ISO string with time, use it directly
    if (date.includes('T') || date.includes('Z')) {
      dateObj = new Date(date);
    } else {
      // If it's YYYY-MM-DD, convert to ISO string for BusinessDateFormats
      dateObj = new Date(`${date}T00:00:00`);
    }
  } else {
    dateObj = date;
  }
  
  if (Number.isNaN(dateObj.getTime())) return '';
  
  return BusinessDateFormats.dateWithYear(dateObj.toISOString(), isFullMonth);
};

/**
 * Formats a date showing month and day for current year, or month, day, and year for other years
 * @param {string|Date} date - Date to format
 * @param {boolean} isFullMonth - Whether to use full month name
 * @returns {string} - Formatted date (e.g., "Jul 15" for current year, "Jul 15, 2022" for other years)
 */
export const formatDateNoCurrentYear = (date, isFullMonth = false) => {
  if (!date) return '';
  
  // Convert to Date object if it's a string
  let dateObj;
  if (typeof date === 'string') {
    // If it's already an ISO string with time, use it directly
    if (date.includes('T') || date.includes('Z')) {
      dateObj = new Date(date);
    } else {
      // If it's YYYY-MM-DD, convert to ISO string for BusinessDateFormats
      dateObj = new Date(`${date}T00:00:00`);
    }
  } else {
    dateObj = date;
  }
  
  if (Number.isNaN(dateObj.getTime())) return '';
  
  return BusinessDateFormats.dateNoCurrentYear(dateObj.toISOString(), isFullMonth);
};
